import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";

/**
 * WinBackOfferContext
 *
 * App-wide state for the platform-funded win-back offer: is one live, when does it end, and
 * where does "Shop now" go. Consumed by the sticky banner, by the PDP's COD surfaces, and by
 * anything else that needs to know an offer is running.
 *
 * WHY A CONTEXT AND NOT THE CART RESPONSE. The cart response carries the authoritative
 * `codBlockedReason` and `couponValidTill`, but two of the surfaces that must react to an offer
 * live on the PDP, which never loads the cart. Threading a cart fetch onto the PDP to answer
 * "is an offer live" would be a lot of request for one boolean. So the offer's own endpoint is
 * the source: it returns validTillEpochMs at mint time, this context stores it, and every
 * surface reads from here. The cart response stays authoritative AT the cart -- see
 * CouponField/CustomerCart, which use its fields directly rather than this.
 *
 * THE COUNTDOWN IS SERVER-DRIVEN, and that is the whole point of storing an absolute instant.
 * `validTillEpochMs` comes from the server's clock at mint time. Remaining time is ALWAYS
 * recomputed as (validTillEpochMs - Date.now()) on mount and on every tick -- never
 * `Date.now() + 60min`, which would silently restart the offer on every navigation and let a
 * shopper hold a 5% discount indefinitely by refreshing.
 *
 * PERSISTED so a refresh or a route change does not reset it. Stored per STORE: this is a
 * multi-tenant SPA and an offer minted on one storefront must not appear on another.
 *
 * Lives in Hooks/ alongside AuthContext.js and FloatingVideoContext.jsx -- there is no
 * Contexts/ directory in this repo.
 */

const WinBackOfferContext = createContext(null);

const STORAGE_PREFIX = "winBackOffer:";

/** Ticks the countdown. 1s is the coarsest interval that still renders MM:SS honestly. */
const TICK_MS = 1000;

const storageKeyFor = (storeKey) => `${STORAGE_PREFIX}${storeKey || "unknown"}`;

/**
 * Reads the stored offer, discarding anything already expired or malformed.
 *
 * Every access is try/caught: localStorage throws outright in some contexts (private windows
 * with site data blocked, embedded webviews), and a storage failure must never take down a
 * storefront over a promotional banner.
 */
function readStored(storeKey) {
  try {
    const raw = window.localStorage.getItem(storageKeyFor(storeKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const validTillEpochMs = Number(parsed?.validTillEpochMs);
    if (!Number.isFinite(validTillEpochMs) || validTillEpochMs <= Date.now()) {
      return null;
    }
    return {
      validTillEpochMs,
      categoryId: parsed?.categoryId ?? null,
      categoryName: parsed?.categoryName ?? null,
      percent: parsed?.percent ?? null,
    };
  } catch {
    return null;
  }
}

function writeStored(storeKey, offer) {
  try {
    if (!offer) {
      window.localStorage.removeItem(storageKeyFor(storeKey));
      return;
    }
    window.localStorage.setItem(storageKeyFor(storeKey), JSON.stringify(offer));
  } catch {
    // Non-fatal: the offer still works for this page view, it just will not survive a refresh.
  }
}

export function WinBackOfferProvider({ children }) {
  const storeDetails = useSelector((state) => state.storeDetails);
  // Store identity for the storage key. userName is the storefront's own slug; id is the
  // fallback for a store shape that has not resolved a name yet.
  const storeKey =
    storeDetails?.storeUserName || storeDetails?.userName || storeDetails?.userId || null;

  const [offer, setOffer] = useState(null);
  const [msLeft, setMsLeft] = useState(0);

  // Hydrate from storage whenever the resolved store changes. Reading on store change rather
  // than once on mount matters: the tenant resolves asynchronously in App.js, so on first paint
  // storeKey is usually still null.
  useEffect(() => {
    const stored = readStored(storeKey);
    setOffer(stored);
    setMsLeft(stored ? Math.max(0, stored.validTillEpochMs - Date.now()) : 0);
  }, [storeKey]);

  // One interval for the whole app. Recomputes from the stored absolute instant every tick, so
  // a backgrounded tab, a sleeping phone or a slow frame cannot drift the countdown -- it is a
  // subtraction, not a decrement.
  useEffect(() => {
    if (!offer) return undefined;

    const tick = () => {
      const remaining = offer.validTillEpochMs - Date.now();
      if (remaining <= 0) {
        // At zero: drop the banner and clear storage, with NO reload. An expired coupon already
        // stops applying server-side (CouponService re-validates the window on every read), so
        // there is nothing to re-fetch -- reloading would only throw away the page they are on.
        setMsLeft(0);
        setOffer(null);
        writeStored(storeKey, null);
        return;
      }
      setMsLeft(remaining);
    };

    tick();
    const handle = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(handle);
  }, [offer, storeKey]);

  /**
   * Records an offer returned by POST /similar-products-offer.
   *
   * Accepts the response payload as-is. `offered` false, a missing instant, or an instant
   * already in the past all clear rather than store, so a stale mint response can never
   * resurrect a dead banner.
   */
  const recordOffer = useCallback(
    (data) => {
      const validTillEpochMs = Number(data?.validTillEpochMs);
      if (!data?.offered || !Number.isFinite(validTillEpochMs) || validTillEpochMs <= Date.now()) {
        return;
      }
      const next = {
        validTillEpochMs,
        categoryId: data?.categoryId ?? null,
        categoryName: data?.categoryName ?? null,
        percent: data?.percent ?? null,
      };
      setOffer(next);
      setMsLeft(Math.max(0, validTillEpochMs - Date.now()));
      writeStored(storeKey, next);
    },
    [storeKey],
  );

  /** Clears the offer -- used when the server says it is gone (e.g. consumed on an order). */
  const clearOffer = useCallback(() => {
    setOffer(null);
    setMsLeft(0);
    writeStored(storeKey, null);
  }, [storeKey]);

  const value = useMemo(
    () => ({
      offer,
      // `isActive` rather than `Boolean(offer)` at every call site: msLeft > 0 is the same
      // condition the banner renders on, so consumers cannot disagree with it.
      isActive: Boolean(offer) && msLeft > 0,
      msLeft,
      percent: offer?.percent ?? null,
      categoryId: offer?.categoryId ?? null,
      categoryName: offer?.categoryName ?? null,
      recordOffer,
      clearOffer,
    }),
    [offer, msLeft, recordOffer, clearOffer],
  );

  return (
    <WinBackOfferContext.Provider value={value}>{children}</WinBackOfferContext.Provider>
  );
}

/**
 * Returns null outside a provider rather than throwing. Several of these surfaces (PDP payment
 * row, pincode check) are shared with contexts that have no provider above them, and a hard
 * throw there would take a page down over a promotional banner.
 */
export function useWinBackOffer() {
  return useContext(WinBackOfferContext);
}

/** MM:SS from a millisecond remainder. Clamps at 0 -- never renders a negative clock. */
export function formatOfferCountdown(ms) {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

export default WinBackOfferContext;
